import VelocityCalculator from './lib/velocity-calculator.js';
import { DRAG_IGNORE_SELECTOR } from './utils/utils.js';

/**
 * Handles all drag interactions with the carousel.
 *
 * Gesture model (idle → armed → dragging):
 * - pointerdown only *arms* the gesture: record the origin and capture the
 *   pointer. It does NOT preventDefault/stopPropagation, so native controls
 *   (a `<select>`, inputs, buttons, links, custom focusable widgets) keep
 *   working — a press that never becomes a horizontal drag is just a click.
 * - pointermove *promotes* to a real drag once it passes the threshold and is
 *   horizontal. Only then do we preventDefault and emit `drag:start`.
 * - Vertical intent on touch is handed to the browser via `touch-action:
 *   pan-y` (set in base.css); the browser scrolls and fires `pointercancel`,
 *   which we treat as a clean, no-op end.
 *
 * @class DragHandler
 */
export default class DragHandler {
	/**
	 * Creates a new drag handler for the carousel
	 * @param {Object} ctx - The context object containing carousel references and services
	 */
	constructor(ctx) {
		const _ = this;
		/** @type {Object} - Reference to context object */
		_.ctx = ctx;

		/** @type {HTMLElement} - Element to bind drag events to */
		_.track = ctx.track;

		/** @type {VelocityCalculator} - Handles velocity calculation with rAF timing */
		_.velocityCalculator = new VelocityCalculator();

		/** @type {number} - Pixels of horizontal movement required to promote a press into a drag */
		_.dragThreshold = 3;

		/**
		 * @type {Object} - Object containing all drag state information
		 */
		_.drag = {
			/** @type {boolean} - Pointer is down and a gesture is being evaluated */
			armed: false,
			/** @type {boolean} - Gesture has been promoted to an active drag */
			isDragging: false,
			/** @type {number|null} - pointerId of the gesture we're tracking */
			pointerId: null,
			/** @type {number} - screenX at the (re-based) drag origin */
			startX: 0,
			/** @type {number} - screenY at pointerdown */
			startY: 0,
			/** @type {number} - Current X position during drag */
			currentPos: 0,
			/** @type {number} - Previous X position (for per-move velocity deltas) */
			prevPos: 0,
			/** @type {number} - Distance moved since the drag origin */
			delta: 0,
			/** @type {number} - Speed of movement */
			velocity: 0,
			/** @type {boolean} - Whether the gesture became a real drag (suppresses the trailing click) */
			dragThresholdMet: false,
		};

		/** @type {Object} - Bound event handlers for proper cleanup */
		_.handlers = {
			click: (e) => _.handleClick(e),
			pointerdown: (e) => _.handleDragStart(e),
			pointermove: (e) => _.handleDragMove(e),
			pointerup: (e) => _.handleDragEnd(e),
			pointercancel: (e) => _.handleDragEnd(e),
			lostpointercapture: (e) => _.handleDragEnd(e),
			// Axis lock: touch-action: pan-y makes the *initial* scroll-vs-drag
			// decision, but once we've committed to a horizontal drag the page
			// can still creep vertically. preventDefault() on a pointer event
			// won't stop a touch-action-permitted scroll — only preventing the
			// touchmove does. Lock the page only while actively dragging.
			touchmove: (e) => {
				if (_.drag.isDragging) e.preventDefault();
			},
			// Cancel the browser's native drag-and-drop (images and links are
			// draggable by default) so grabbing an <img>/<a> drags the carousel
			// instead of starting a native DnD. Skip when the carousel isn't
			// draggable, or inside a region the dev opted out of (DRAG_IGNORE),
			// so native DnD still works there.
			dragstart: (e) => {
				if (!_.ctx.store.getOptions().draggable) return;
				if (e.target.closest(DRAG_IGNORE_SELECTOR)) return;
				e.preventDefault();
			},
			dblclick: (e) => {
				e.preventDefault();
				e.stopPropagation();
				return false;
			},
			optionsChanged: () => _.syncDraggableAttr(),
		};

		_.init();
	}

	/**
	 * Initialize the drag handler
	 */
	init() {
		this.bindEvents();
		this.syncDraggableAttr();
	}

	syncDraggableAttr() {
		const draggable = this.ctx.store.getOptions().draggable;
		this.track.classList.toggle('tarot-drag-disabled', !draggable);
	}

	/**
	 * Bind drag events on the carousel track element.
	 */
	bindEvents() {
		const _ = this;
		const track = _.track;

		track.addEventListener('click', _.handlers.click);
		track.addEventListener('pointerdown', _.handlers.pointerdown, { passive: false });
		track.addEventListener('pointermove', _.handlers.pointermove, { passive: false });
		track.addEventListener('pointerup', _.handlers.pointerup, { passive: false });
		track.addEventListener('pointercancel', _.handlers.pointercancel, { passive: false });
		track.addEventListener('lostpointercapture', _.handlers.lostpointercapture);
		track.addEventListener('touchmove', _.handlers.touchmove, { passive: false });
		track.addEventListener('dragstart', _.handlers.dragstart);

		// Prevent double-tap zoom / double-click
		track.addEventListener('dblclick', _.handlers.dblclick);

		_.ctx.emitter.on(_.ctx.events.store.optionsChanged, _.handlers.optionsChanged);
	}

	/**
	 * Handle click events on the track.
	 * Suppresses the click that trails a real drag; otherwise emits slides:click.
	 * @param {Event} e - The click event.
	 */
	handleClick(e) {
		const _ = this;

		// the gesture became a drag - swallow the one trailing click, then
		// clear the flag so a later keyboard/programmatic click isn't eaten
		if (_.drag.dragThresholdMet) {
			_.drag.dragThresholdMet = false;
			e.preventDefault();
			return;
		}

		// emit click event
		const slide = e.target.closest('tarot-slide');
		if (slide) {
			const index = parseInt(slide.getAttribute('index')) || 0;
			const renderIndex = slide._renderIndex;
			_.ctx.emitter.emit(_.ctx.events.slides.click, {
				index,
				renderIndex,
				event: e,
			});
		}
	}

	/**
	 * Handle pointerdown: arm the gesture without hijacking the pointer.
	 * @param {PointerEvent} e - The pointer down event.
	 */
	handleDragStart(e) {
		const _ = this;
		const drag = _.drag;

		// any new press clears stale click-suppression from a previous drag
		drag.dragThresholdMet = false;

		// exit if dragging is disabled
		if (!_.ctx.store.getOptions().draggable) return;

		// ignore secondary pointers (e.g. a second finger) while one is active
		if (drag.armed) return;

		// Don't arm/capture on elements that need the native pointer/caret
		// (form fields) or that a dev opted out with [data-tarot-no-drag].
		// Click-activated controls (a, button, label) are intentionally NOT
		// guarded: in the deferred model a tap is still their click and a
		// past-threshold drag still drags the carousel.
		if (e.target.closest(DRAG_IGNORE_SELECTOR)) return;

		// ARM — record the origin, do NOT preventDefault/stopPropagation
		drag.armed = true;
		drag.isDragging = false;
		drag.pointerId = e.pointerId;
		drag.startX = e.screenX;
		drag.startY = e.screenY;
		drag.currentPos = e.screenX;
		drag.prevPos = e.screenX;
		drag.velocity = 0;
		drag.delta = 0;

		// NOTE: pointer capture is deliberately deferred to promotion (see
		// handleDragMove). A press that stays a tap is never captured, so
		// capture can never interfere with a control's native click.

		// Warm the velocity calculator from pointerdown so a fast flick that
		// crosses the threshold immediately still reports accurate velocity.
		_.velocityCalculator.start();
	}

	/**
	 * Handle pointermove: decide tap-vs-drag, then drive the active drag.
	 * @param {PointerEvent} e - The pointer move event.
	 */
	handleDragMove(e) {
		const _ = this;
		const drag = _.drag;

		if (!drag.armed || e.pointerId !== drag.pointerId) return;

		// keep velocity warm even before promotion
		const posDelta = e.screenX - drag.prevPos;
		drag.prevPos = e.screenX;
		_.velocityCalculator.addDelta(posDelta);

		if (!drag.isDragging) {
			const dx = Math.abs(e.screenX - drag.startX);
			const dy = Math.abs(e.screenY - drag.startY);

			// vertical-dominant intent → it's a page scroll, not a drag.
			// touch-action: pan-y also makes the browser scroll + fire
			// pointercancel; bail proactively so we never hijack a scroll.
			if (dy > dx) return;

			// still inside the dead zone — could yet be a tap/click
			if (dx <= _.dragThreshold) return;

			// PROMOTE: rebase the origin to *here* so TrackAnimator's
			// (#dragStartPos + drag.delta) starts at zero with no jump.
			drag.startX = e.screenX;
			drag.currentPos = e.screenX;
			drag.delta = 0;
			drag.isDragging = true;
			drag.dragThresholdMet = true;

			// Explicit capture is only needed for mouse/pen — to keep move/up
			// flowing when the cursor leaves the track. Touch pointers already
			// have *implicit* capture (per spec, captured to the target on
			// pointerdown until pointerup); calling setPointerCapture again
			// mid-gesture on touch is redundant and triggers a spurious
			// pointercancel/lostpointercapture on several mobile engines, which
			// would end the drag the instant it starts. Capturing at promotion
			// (not pointerdown) also means a tap never captures, so it can't
			// disturb a control's native click.
			if (e.pointerType !== 'touch') {
				try {
					_.track.setPointerCapture(e.pointerId);
				} catch {
					// best-effort — ignore if capture is unavailable
				}
			}

			_.ctx.emitter.emit(_.ctx.events.drag.start, { event: e, drag });
		}

		// active drag
		e.preventDefault();
		drag.currentPos = e.screenX;
		drag.delta = drag.currentPos - drag.startX;
		drag.velocity = _.velocityCalculator.getVelocity();
		_.ctx.emitter.emit(_.ctx.events.drag.move, { event: e, drag });
	}

	/**
	 * Handle pointerup / pointercancel / lostpointercapture: finalize.
	 * A press that never promoted is a tap — left completely untouched so
	 * the native click (and slides:click) proceed normally.
	 * @param {PointerEvent} e - The terminating pointer event.
	 */
	handleDragEnd(e) {
		const _ = this;
		const drag = _.drag;

		if (!drag.armed || e.pointerId !== drag.pointerId) return;

		// release the pointer capture if we still hold it
		try {
			if (_.track.hasPointerCapture?.(drag.pointerId)) {
				_.track.releasePointerCapture(drag.pointerId);
			}
		} catch {
			// ignore
		}

		const wasDragging = drag.isDragging;
		drag.armed = false;
		drag.isDragging = false;
		drag.pointerId = null;

		if (!wasDragging) {
			// never promoted → a tap. Don't emit drag:end (TrackAnimator never
			// got drag:start, so a settle here would be spurious and would
			// wrongly pause autoscroll). Just stop the warm velocity calc.
			_.velocityCalculator.stop();
			return;
		}

		// finalize a real drag
		drag.velocity = _.velocityCalculator.stop();
		_.ctx.emitter.emit(_.ctx.events.drag.end, { event: e, drag });
		drag.delta = 0;
	}

	/**
	 * Clean up event listeners and cancel any pending operations
	 * Should be called when the carousel is destroyed to prevent memory leaks
	 */
	destroy() {
		const _ = this;
		const track = _.track;

		// Stop velocity calculator if running
		_.velocityCalculator.stop();

		// release any outstanding pointer capture
		try {
			if (_.drag.pointerId != null && track.hasPointerCapture?.(_.drag.pointerId)) {
				track.releasePointerCapture(_.drag.pointerId);
			}
		} catch {
			// ignore
		}

		// Remove all event listeners using stored handlers
		track.removeEventListener('click', _.handlers.click);
		track.removeEventListener('pointerdown', _.handlers.pointerdown);
		track.removeEventListener('pointermove', _.handlers.pointermove);
		track.removeEventListener('pointerup', _.handlers.pointerup);
		track.removeEventListener('pointercancel', _.handlers.pointercancel);
		track.removeEventListener('lostpointercapture', _.handlers.lostpointercapture);
		track.removeEventListener('touchmove', _.handlers.touchmove);
		track.removeEventListener('dragstart', _.handlers.dragstart);
		track.removeEventListener('dblclick', _.handlers.dblclick);

		_.ctx.emitter.off(_.ctx.events.store.optionsChanged, _.handlers.optionsChanged);
	}
}
